import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {Icon, IconButton, Input, ShowBelowViewport, DarkOverlay} from '_uiComponents'
import {Box, BoxProps, FocusLock, HStack, IconButtonProps, IconProps, StackProps, Text, TextProps, useMultiStyleConfig} from '@chakra-ui/react'
import {HeaderActionType, useHeaderDispatch} from '_context'
import {useSearchSuggestions} from '@salesforce/commerce-sdk-react'
import {searchUrlBuilder, mapSearchSectionPageDesignerData} from '_utils'
import {FormattedSuggestions, SuggestionResult} from '_types'
import {RemoveScroll} from 'react-remove-scroll'
import {remCalc} from '_themeUtils'
import {useAnalytics, useHeaderSize, usePageDesigner, useViewport} from '_customHooks'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import debounce from 'lodash/debounce'
import SearchSuggestions from './partials/suggestions'
import SearchResults from './partials/results'

interface SearchSectionProps {
    isAtTop: boolean
}

/**
 * @name SearchSection
 *
 * @component
 *
 * @description
 * This component displays a search input and search suggestions, and handles search results rendering.
 * It also includes a dark overlay and handles mobile-specific behavior that converts it to a simil-modal container.
 *
 * @remarks
 * Content is partially retrieved via page designer.
 * For maximum performance it's using a debouncing technique for search input state handle.
 *
 * @param {SearchSectionProps} props - Properties object.
 * @param {boolean} props.isAtTop - Indicates if user has scrolled to the top or not.
 *
 * @returns {React.FC<SearchSectionProps>} - The rendered SearchSection component.
 */
const SearchSection: React.FC<SearchSectionProps> = ({isAtTop}) => {
    const {headerSize} = useHeaderSize(!isAtTop)
    const styles = useMultiStyleConfig('SearchSection', {headerSize}) as Record<string, BoxProps & StackProps & IconButtonProps & TextProps & IconProps>
    const {dispatch} = useHeaderDispatch()
    const {formatMessage} = useIntl()
    const {isDesktop} = useViewport()
    const {locale} = useMultiSite()
    const navigate = useNavigation()
    const {analyticsEventInternalSearch} = useAnalytics()

    const {pageComponents, linkObject, isLoading: isPageLoading} = usePageDesigner({pageId: 'search-section-pwa'})
    const {ideasData, womenData, menData, productsData} = mapSearchSectionPageDesignerData(linkObject, pageComponents)

    const SEARCH_MIN_LENGTH = 3
    const SEARCH_MAX_LENGTH = 8
    const DEBOUNCE_TIME = 600
    const forbiddenCharacters = /[<>&%()|!$^`?]/

    const searchInputRef = useRef<HTMLInputElement | null>(null)
    const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>('')
    const [isSearchError, setIsSearchError] = useState<boolean>(false)

    const debouncedSearch = useCallback(
        debounce((input) => {
            setDebouncedSearchValue(input)
        }, DEBOUNCE_TIME),
        [],
    )

    const {data: searchSuggestionsData} = useSearchSuggestions(
        {
            parameters: {
                q: debouncedSearchValue,
                locale: locale.id,
                limit: SEARCH_MAX_LENGTH,
                c_headerSuggestions: true,
            },
        },
        {
            enabled: debouncedSearchValue.length >= SEARCH_MIN_LENGTH,
            keepPreviousData: true,
        },
    )

    const searchSuggestions = useMemo(() => formatSuggestions(searchSuggestionsData), [searchSuggestionsData])

    const searchTermSuggestion = useMemo(() => {
        const firstSuggestionAvailable = searchSuggestionsData?.productSuggestions?.suggestedPhrases?.[0]
        if (!firstSuggestionAvailable?.exactMatch) return firstSuggestionAvailable?.phrase
    }, [searchSuggestionsData])

    const searchErrorLabel = formatMessage({
        id: 'error.search.charcontrol',
        defaultMessage: 'Error: no special characters are allowed in the search field. Please try again.',
    })
    const placeholderLabel = formatMessage({id: 'label.header.searchwatermark', defaultMessage: 'What are you looking for?'})
    const closeButtonLabel = formatMessage({id: 'alt.link.close', defaultMessage: 'Close'})
    const searchInputAriaLabel = formatMessage({id: 'simplesearch.searchtext', defaultMessage: 'Enter a keyword or article code'})

    const onSearchInputChange = (input: string) => {
        if (forbiddenCharacters.test(input)) {
            setIsSearchError(true)
        } else {
            setIsSearchError(false)

            if (input.length < SEARCH_MIN_LENGTH) {
                setDebouncedSearchValue(input)
            } else if (input.length >= SEARCH_MIN_LENGTH) {
                debouncedSearch(input)
            } else if (input.length === 0) {
                debouncedSearch.cancel()
                setDebouncedSearchValue('')
            }
        }
    }

    const closeSearchSection = (isSubmitFlow?: boolean) => {
        const seachTrigger = document.querySelector('#tobpar-search-trigger') as HTMLButtonElement
        dispatch({type: HeaderActionType.TOGGLE_SEARCH_SECTION})
        if (!isSubmitFlow) {
            seachTrigger?.focus()
        }
    }

    const navigateToSearchPage = (event?: React.MouseEvent<HTMLAnchorElement>, isSubmitFlow?: boolean) => {
        event?.preventDefault()
        const searchText = searchInputRef.current?.value.trim()

        if (!searchText || searchText.length < 1 || forbiddenCharacters.test(searchText)) {
            return
        }

        if (isSubmitFlow) {
            analyticsEventInternalSearch({action: 'Tips', label: searchText})
        }

        navigate(searchUrlBuilder(searchText, locale.alias))
        closeSearchSection(isSubmitFlow)
    }

    const onSubmitSearch = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        navigateToSearchPage(undefined, true)
    }

    /**
     * Focus on search input on component mount.
     */
    useEffect(() => {
        searchInputRef.current?.focus()
    }, [])

    /**
     * Cancel debounce on component unmount.
     */
    useEffect(() => {
        return () => {
            debouncedSearch.cancel()
        }
    }, [debouncedSearch])

    return (
        <Box
            data-testid="search-section"
            id="search-section"
            {...styles.container}
            as={RemoveScroll}
            enabled={!isDesktop}
            onKeyDown={(e) => {
                if (e.key === 'Escape') {
                    closeSearchSection()
                }
            }}
        >
            <FocusLock persistentFocus={false}>
                <DarkOverlay variant="headerMenu" />

                <ShowBelowViewport breakpoint="lg">
                    <HStack {...styles.mobileHeader}>
                        <IconButton icon={<Icon name="IconClose" size="md" />} aria-label={closeButtonLabel} onClick={() => closeSearchSection()} />
                    </HStack>
                </ShowBelowViewport>

                <Box data-testid="search-form" as="form" {...styles.searchBox} onSubmit={onSubmitSearch}>
                    <Input
                        ref={searchInputRef}
                        name="search"
                        type="text"
                        leftIcon={<Icon name="IconSearch" size="lg" ml={remCalc(-12)} />}
                        placeholder={placeholderLabel}
                        variant="search"
                        onChange={(e) => onSearchInputChange(e.target.value)}
                        aria-autocomplete="list"
                        aria-haspopup="listbox"
                        aria-expanded={false}
                        aria-controls="search-section"
                        aria-owns="search-section"
                        aria-label={searchInputAriaLabel}
                    />

                    {isSearchError && (
                        <Text data-testid="search-input-error" {...styles.searchErrorLabel}>
                            {searchErrorLabel}
                        </Text>
                    )}
                </Box>

                {debouncedSearchValue.length < SEARCH_MIN_LENGTH ? (
                    <SearchSuggestions ideas={ideasData} women={womenData} men={menData} products={productsData} isPageLoading={isPageLoading} />
                ) : (
                    searchSuggestions?.products !== undefined && (
                        <SearchResults
                            searchValue={debouncedSearchValue}
                            productSuggestionIds={searchSuggestions.products}
                            searchSuggestion={searchTermSuggestion}
                            ideas={ideasData}
                            storiesSuggestions={searchSuggestions.stories}
                            onResultClick={navigateToSearchPage}
                        />
                    )
                )}
            </FocusLock>
        </Box>
    )
}

/**
 * Handles the formatting for useSearchSuggestions response.
 *
 * @param {SuggestionResult | undefined} searchSuggestions - useSearchSuggestions response
 * @returns {FormattedSuggestions} obj - An object containing the formatted suggestions.
 * @returns {Array<string> | undefined} obj.products - Products array (these are IDs).
 * @returns {Array<StorySuggestion> | undefined} obj.stories - Stories array.
 */
const formatSuggestions = (searchSuggestions: SuggestionResult | undefined): FormattedSuggestions => {
    return {
        products: searchSuggestions?.productSuggestions?.products?.map((product) => product.productId),
        stories: searchSuggestions?.contentSuggestions?.c_stories,
    }
}

export default SearchSection
